import dayjs from 'dayjs';

/**
 * NewsItem story definition
 * @typedef {Object} NewsItem
 * @property {string} [category] - Category
 * @property {"NewsItem"} component - Type of component
 * @property {string} datePublished - Formatted time
 * @property {string|null} [image] - Image source
 * @property {string} link - Link to external resource
 * @property {string} [location] - Internal data modifier
 * @property {string} [publisherLogo] - Image source for publisher
 * @property {string} [publisherName] - Name of the publisher
 * @property {string} [quote] - News excerpt
 * @property {string} title - News title
 */

/**
 * Page story definition
 * @typedef {Object} Page
 * @property {string} [category] - News category
 * @property {"page"} component - Type of component
 * @property {string} [datePublished] - Formatted time
 * @property {string} [description] - News description
 * @property {string} [og_image] - Image source with prepended "https:"
 * @property {string} title - News title
 */

/**
 * Normalized News meta information
 * @typedef {Object} NewsMeta
 * @property {string} [category] - News category
 * @property {string} datePublished - Published date in ISO format
 * @property {string} [image] - Image source
 * @property {string} [link] - News href
 * @property {string} [publisherName] - Name of the publisher
 * @property {string} [text] - News description
 * @property {string} title - News title
 * @property {string} uid
 */

/**
 * Parses Page or NewsItem story into unified data
 * @param {Object} story
 * @param {NewsItem|Page} story.content
 * @param {string} story.first_published_at - Published date in ISO format
 * @param {string} story.full_slug - Location of the story
 * @param {string} story.uuid - Story ID
 * @returns {NewsMeta|null}
 */
const parseNewsContent = (story) => {
  const { content, uuid } = story;

  if (content?.component === 'NewsItem') {
    return {
      category: content.category || '',
      datePublished: dayjs(content.datePublished).toISOString(),
      image: content.image || '',
      link: content.link,
      publisherName: content.publisherName,
      text: content.quote,
      title: content.title,
      uid: uuid,
    };
  }

  if (content?.component === 'page') {
    return {
      category: content.category || '',
      datePublished: content.datePublished || story.first_published_at,
      image: content.og_image && content.og_image.replace('https:', ''),
      link: story.full_slug,
      text: content.description,
      title: content.title,
      uid: uuid,
    };
  }

  return null;
};

const parseNewsFields = (story) => {
  return {
    category: story.field_category_string || '',
    datePublished: story.field_datePublished_string,
    link: story.field_link_string || story.full_slug,
    text: story.field_quote_string || story.field_description_string,
    title: story.field_title_string,
    uid: story.uuid,
  };
};

const parseNews = (story) =>
  story?.content ? parseNewsContent(story) : parseNewsFields(story);

export default parseNews;
